import dayjs from '~/timeInit';

import type { PricingOptionIndex } from '../../types';

import { computeNightlySurcharge, parseIsoDateUtc, weekdayOf } from './computeNightlySurcharge';
import type {
  SeasonGridKey,
  SurchargeCollectedBy,
  SurchargeGrids,
  SurchargePeriod,
  SurchargeSessionState,
  TravelMonth,
  Weekday,
  WeekdayAmounts,
  WeekdayCosts,
} from './types';

/** Monday-first, matching Salesforce's `Day_of_the_Week__c` ordering. */
export const WEEKDAYS: ReadonlyArray<Weekday> = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

export const WEEKDAY_LABELS: Record<Weekday, string> = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
  sun: 'Sun',
};

export const SURCHARGE_COLLECTED_BY_OPTIONS: ReadonlyArray<SurchargeCollectedBy> = [
  'Paid at Booking Stage',
  'Paid directly to the Hotel',
];

export const ALL_PRICING_OPTIONS: ReadonlyArray<PricingOptionIndex> = [0, 1, 2];

const MONTH_KEY_FORMAT = 'YYYY-MM';

const MS_PER_DAY = 86_400_000;

export const SEASON_GRIDS: ReadonlyArray<{ key: SeasonGridKey; label: string }> = [
  { key: 'allYearRound', label: 'All Year Round' },
  { key: 'high', label: 'High' },
  { key: 'shoulder', label: 'Shoulder' },
];

function emptyWeekdayAmounts(): WeekdayAmounts {
  return { mon: 0, tue: 0, wed: 0, thu: 0, fri: 0, sat: 0, sun: 0 };
}

function emptyWeekdayCosts(): WeekdayCosts {
  return { mon: null, tue: null, wed: null, thu: null, fri: null, sat: null, sun: null };
}

export function createEmptySurchargeSession(): SurchargeSessionState {
  return {
    grids: {
      allYearRound: emptyWeekdayAmounts(),
      high: emptyWeekdayAmounts(),
      shoulder: emptyWeekdayAmounts(),
      seasonByMonth: {},
      periods: [],
    },
    costs: {
      allYearRound: emptyWeekdayCosts(),
      high: emptyWeekdayCosts(),
      shoulder: emptyWeekdayCosts(),
    },
  };
}

/**
 * Weekday-grid costs are typed in the opportunity's local currency and carry no currency of their
 * own, so they go stale when it changes. Dated periods carry their own currency and stay valid.
 */
export function hasTypedCost(session: SurchargeSessionState): boolean {
  return Object.values(session.costs).some((weekdays) => Object.values(weekdays).some((cost) => cost != null));
}

export function clearTypedCosts(session: SurchargeSessionState): SurchargeSessionState {
  return { ...session, costs: createEmptySurchargeSession().costs };
}

export function periodCostAud(
  period: Pick<SurchargePeriod, 'cost' | 'currency'>,
  audRateFor: (currency: string) => number | null,
): number | null {
  if (period.cost == null || !Number.isFinite(period.cost)) {
    return null;
  }
  const rate = audRateFor(period.currency);
  return rate && rate > 0 ? period.cost / rate : null;
}

export function isSeasonApplied(seasonKey: SeasonGridKey, grids: SurchargeGrids): boolean {
  return seasonKey === 'allYearRound' || Object.values(grids.seasonByMonth).includes(seasonKey);
}

/**
 * Distinct YYYY-MM covered by the stays, ascending. Every night is walked, not just the
 * stay-start: pricing looks up the month each night falls in, so offering only the start months
 * would leave a month the engine prices but the CM can never mark High or Shoulder.
 */
export function travelMonthsFrom(isoDates: ReadonlyArray<string>, nights = 1): Array<TravelMonth> {
  const keys = new Set<string>();
  const nightCount = Number.isInteger(nights) && nights > 0 ? nights : 1;

  for (const isoDate of isoDates) {
    const checkIn = parseIsoDateUtc(isoDate);
    if (checkIn == null) {
      continue;
    }

    for (let night = 0; night < nightCount; night++) {
      const stayed = new Date(checkIn.getTime());
      stayed.setUTCDate(stayed.getUTCDate() + night);
      keys.add(stayed.toISOString().slice(0, 7));
    }
  }

  return Array.from(keys)
    .sort()
    .map((key) => ({ key, label: dayjs.utc(key, MONTH_KEY_FORMAT, true).format('MMM YYYY') }));
}

/**
 * Days a period actually surcharges. With no weekdays picked this is the doc's `to - from + 1`;
 * with weekdays picked it counts only the matching nights, which is the figure the guest pays on.
 */
export function daysSurchargedIn(period: SurchargePeriod): number | null {
  const start = parseIsoDateUtc(period.startDate);
  const end = parseIsoDateUtc(period.endDate);
  if (start == null || end == null || start > end) {
    return null;
  }

  const totalDays = Math.round((end.getTime() - start.getTime()) / MS_PER_DAY) + 1;
  const picked = new Set(period.weekdays);
  if (picked.size === 0) {
    return totalDays;
  }

  // Counted in closed form rather than walked day by day, because a date input accepts years up to
  // 275760 and this runs in render for every period on screen.
  let days = picked.size * Math.floor(totalDays / 7);
  for (let offset = 0; offset < totalDays % 7; offset++) {
    const weekday = weekdayOf(new Date(start.getTime() + offset * MS_PER_DAY).toISOString().slice(0, 10));
    if (weekday != null && picked.has(weekday)) {
      days++;
    }
  }
  return days;
}

/**
 * Share of the month's calendar days carrying a surcharge, as a fraction. Driven through the
 * engine rather than counting weekdays, so the month's season and any periods both count.
 */
export function percentDaysSurcharged(monthKey: string, grids: SurchargeGrids): number | null {
  // Strict parsing, because a lenient dayjs reads '2026-13' as valid and reports 31 days.
  const month = dayjs.utc(monthKey, MONTH_KEY_FORMAT, true);
  if (!month.isValid()) {
    return null;
  }

  const total = month.daysInMonth();

  let surcharged = 0;
  for (let day = 1; day <= total; day++) {
    const isoDate = `${monthKey}-${String(day).padStart(2, '0')}`;
    if (computeNightlySurcharge(isoDate, grids) > 0) {
      surcharged++;
    }
  }

  return surcharged / total;
}
